import { Group } from '@mantine/core';
import { Filter } from '@repo/common/components/Filter';
import { OPERATORS_CONFIG } from '@repo/common/components/Filter/constants';
import { FilterOperator } from '@repo/common/components/Filter/types';
import { observer } from 'mobx-react-lite';
import { useEffect, useMemo } from 'react';
import {
	FilterOptionType,
	SearchFilterV2StoreContext,
	useFilterStoreWithPersistence,
} from '../../../Filter';
import { FILTER_OPTIONS_CONFIG } from '../../../Filter/constants';
import { LineageDirectionEnum } from '../../types';
import { ImpactAnalysisSearch } from './ImpactAnalysisSearch';
import { impactAnalysisStore } from './store';

export const ImpactAnalysisFilters = observer(() => {
	const { searchFilterV2Store } = useFilterStoreWithPersistence({
		filterOptions: [
			FILTER_OPTIONS_CONFIG[FilterOptionType.DIRECTION],
			FILTER_OPTIONS_CONFIG[FilterOptionType.NATIVE_TYPE],
			FILTER_OPTIONS_CONFIG[FilterOptionType.INTEGRATION],
			FILTER_OPTIONS_CONFIG[FilterOptionType.DEPTH],
		],
		filterLocalStorageKey: 'impact-analysis-filters',
	});

	useEffect(() => {
		impactAnalysisStore.setFilterValues(searchFilterV2Store.values);
	}, [searchFilterV2Store.values]);

	const directionFilter = useMemo(
		() =>
			searchFilterV2Store.values.find(
				(v) => v.filterType === FilterOptionType.DIRECTION
			) || {
				filterType: FilterOptionType.DIRECTION,
				value: LineageDirectionEnum.DOWNSTREAM,
				operator: FilterOperator.Is,
				isNotSetApplied: false,
				isSetApplied: false,
			},
		[searchFilterV2Store.values]
	);

	const nativeTypeFilter = useMemo(
		() =>
			searchFilterV2Store.values.find(
				(v) => v.filterType === FilterOptionType.NATIVE_TYPE
			) || {
				filterType: FilterOptionType.NATIVE_TYPE,
				value: [],
				operator: FilterOperator.Is,
				isNotSetApplied: false,
				isSetApplied: false,
			},
		[searchFilterV2Store.values]
	);

	const integrationFilter = useMemo(
		() =>
			searchFilterV2Store.values.find(
				(v) => v.filterType === FilterOptionType.INTEGRATION
			) || {
				filterType: FilterOptionType.INTEGRATION,
				value: [],
				operator: FilterOperator.Is,
				isNotSetApplied: false,
				isSetApplied: false,
			},
		[searchFilterV2Store.values]
	);

	const depthFilter = useMemo(
		() =>
			searchFilterV2Store.values.find(
				(v) => v.filterType === FilterOptionType.DEPTH
			) || {
				filterType: FilterOptionType.DEPTH,
				value: 3,
				operator: FilterOperator.Is,
				isNotSetApplied: false,
				isSetApplied: false,
			},
		[searchFilterV2Store.values]
	);

	const handleFilterChange =
		(filterType: FilterOptionType) => (value: Partial<any>) => {
			const existingIndex = searchFilterV2Store.values.findIndex(
				(v) => v.filterType === filterType
			);

			if (existingIndex !== -1) {
				searchFilterV2Store.onChangeValue(existingIndex)(value);
			} else {
				let defaultValue;
				if (filterType === FilterOptionType.DIRECTION) {
					defaultValue = LineageDirectionEnum.DOWNSTREAM;
				} else if (filterType === FilterOptionType.DEPTH) {
					defaultValue = 3;
				} else {
					defaultValue = [];
				}

				const newFilter = {
					filterType,
					value: value.value ?? defaultValue,
					operator: value.operator ?? FilterOperator.Is,
					isNotSetApplied: value.isNotSetApplied ?? false,
					isSetApplied: value.isSetApplied ?? false,
				};
				searchFilterV2Store.onAddValue(newFilter);
			}
		};

	const handleFilterClear = (filterType: FilterOptionType) => () => {
		const idx = searchFilterV2Store.values.findIndex(
			(v) => v.filterType === filterType
		);
		if (idx !== -1) {
			searchFilterV2Store.onClearValue(idx)();
		}
	};

	return (
		<SearchFilterV2StoreContext.Provider value={searchFilterV2Store}>
			<Group position="apart" noWrap>
				{!impactAnalysisStore.showSearch && (
					<Group spacing="xs">
						<Filter
							filterOption={FILTER_OPTIONS_CONFIG[FilterOptionType.DIRECTION]}
							onChange={handleFilterChange(FilterOptionType.DIRECTION)}
							value={directionFilter}
							showDetailedLabel
							operatorConfig={
								OPERATORS_CONFIG[
									FILTER_OPTIONS_CONFIG[FilterOptionType.DIRECTION]
										.filterDropdownConfig.dropdownType
								]
							}
						/>
						<Filter
							filterOption={FILTER_OPTIONS_CONFIG[FilterOptionType.NATIVE_TYPE]}
							onChange={handleFilterChange(FilterOptionType.NATIVE_TYPE)}
							value={nativeTypeFilter}
							showDetailedLabel
							operatorConfig={
								OPERATORS_CONFIG[
									FILTER_OPTIONS_CONFIG[FilterOptionType.NATIVE_TYPE]
										.filterDropdownConfig.dropdownType
								]
							}
							onClear={handleFilterClear(FilterOptionType.NATIVE_TYPE)}
						/>
						<Filter
							filterOption={FILTER_OPTIONS_CONFIG[FilterOptionType.INTEGRATION]}
							onChange={handleFilterChange(FilterOptionType.INTEGRATION)}
							value={integrationFilter}
							showDetailedLabel
							operatorConfig={
								OPERATORS_CONFIG[
									FILTER_OPTIONS_CONFIG[FilterOptionType.INTEGRATION]
										.filterDropdownConfig.dropdownType
								]
							}
							onClear={handleFilterClear(FilterOptionType.INTEGRATION)}
						/>
						<Filter
							filterOption={FILTER_OPTIONS_CONFIG[FilterOptionType.DEPTH]}
							onChange={handleFilterChange(FilterOptionType.DEPTH)}
							value={depthFilter}
							showDetailedLabel
							operatorConfig={
								OPERATORS_CONFIG[
									FILTER_OPTIONS_CONFIG[FilterOptionType.DEPTH]
										.filterDropdownConfig.dropdownType
								]
							}
							onClear={handleFilterClear(FilterOptionType.DEPTH)}
						/>
					</Group>
				)}
				<ImpactAnalysisSearch
					searchTerm={impactAnalysisStore.searchTerm}
					setSearchTerm={impactAnalysisStore.setSearchTerm}
					hidden={!impactAnalysisStore.showSearch}
				/>
			</Group>
		</SearchFilterV2StoreContext.Provider>
	);
});
